import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { ComponentPropsWithoutRef, ComponentRef } from "react";
import { forwardRef } from "react";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { cn } from "@/shared/utils/cn";

export type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

// State matrix from the Figma selection-control set 216:6985 (2026-08-07
// intake). Anomaly rule applied: the source's #1893D1 checked-focus/disabled
// fill and #CCCCCC/#666666 grays are excluded — checked fill stays
// clm-primary-blue and disabled states use token grays.
export const Checkbox = forwardRef<ComponentRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  function Checkbox({ className, ...props }, ref) {
    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          // Hover is scoped to enabled: so CSS source order can never let it beat disabled:bg-*;
          // disabled+checked/indeterminate get explicit bg/border/text so the mark stays deterministic.
          "peer h-4 w-4 shrink-0 cursor-pointer rounded-sm border border-clm-dark-gray bg-white ring-offset-background transition-colors enabled:data-[state=unchecked]:hover:bg-off-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-clm-primary-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-clm-light-gray disabled:bg-clm-light-gray disabled:text-paving-gray data-[state=checked]:border-clm-primary-blue data-[state=checked]:bg-clm-primary-blue data-[state=checked]:text-white data-[state=indeterminate]:border-clm-primary-blue data-[state=indeterminate]:bg-clm-primary-blue data-[state=indeterminate]:text-white disabled:data-[state=checked]:border-clm-light-gray disabled:data-[state=checked]:bg-clm-light-gray disabled:data-[state=checked]:text-paving-gray disabled:data-[state=indeterminate]:border-clm-light-gray disabled:data-[state=indeterminate]:bg-clm-light-gray disabled:data-[state=indeterminate]:text-paving-gray",
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="group flex items-center justify-center text-current">
          <ClmIcon
            name="checkbox-checked"
            decorative
            className="h-4 w-4 group-data-[state=indeterminate]:hidden"
          />
          <ClmIcon
            name="checkbox-indeterminate"
            decorative
            className="hidden h-4 w-4 group-data-[state=indeterminate]:block"
          />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  },
);
